import { useState, useEffect } from "react";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { createLogger } from "@/lib/utils/logger";
import { supabase } from "@/integrations/supabase/client";

const logger = createLogger('auth', 'SchoolInfoForm');

interface SchoolInfoFormProps {
  schoolDistrict: string;
  school: string;
  customSchoolDistrict: string;
  customSchool: string;
  disabled?: boolean;
  
  // New prop pattern
  onSchoolDistrictChange?: (value: string) => void;
  onSchoolChange?: (value: string) => void;
  onCustomSchoolDistrictChange?: (value: string) => void;
  onCustomSchoolChange?: (value: string) => void;
  
  // Old prop pattern for backward compatibility
  setSchoolDistrict?: (value: string) => void;
  setSchool?: (value: string) => void;
  setCustomSchoolDistrict?: (value: string) => void;
  setCustomSchool?: (value: string) => void;
  
  // Optional props for data sources
  districts?: { value: string; label: string }[];
  schools?: { value: string; label: string }[];
  isLoadingDistricts?: boolean;
}

export function SchoolInfoForm({
  schoolDistrict,
  school,
  customSchoolDistrict,
  customSchool,
  disabled = false,
  
  // Handle both new and old prop patterns
  onSchoolDistrictChange,
  onSchoolChange,
  onCustomSchoolDistrictChange,
  onCustomSchoolChange,
  
  setSchoolDistrict,
  setSchool,
  setCustomSchoolDistrict,
  setCustomSchool,
  
  // Optional data sources with defaults
  districts: providedDistricts,
  schools: providedSchools,
  isLoadingDistricts = false,
}: SchoolInfoFormProps) {
  const [showCustomDistrict, setShowCustomDistrict] = useState(schoolDistrict === "Other");
  const [showCustomSchool, setShowCustomSchool] = useState(school === "Other");
  const [loading, setLoading] = useState(true);
  const [districtsData, setDistrictsData] = useState<{ value: string; label: string }[]>([]);
  const [schoolsData, setSchoolsData] = useState<{ value: string; label: string }[]>([]);
  
  // Log initial props for debugging
  useEffect(() => {
    logger.info(JSON.stringify({ 
      schoolDistrict, 
      school, 
      customSchoolDistrict, 
      customSchool,
      providedDistrictsCount: providedDistricts?.length || 0,
      providedSchoolsCount: providedSchools?.length || 0
    }));
  }, []);
  
  // Function to handle both old and new prop patterns
  const updateSchoolDistrict = (value: string) => {
    if (onSchoolDistrictChange) onSchoolDistrictChange(value);
    if (setSchoolDistrict) setSchoolDistrict(value);
    logger.info(`School district selected: ${value}`);
  };
  
  const updateSchool = (value: string) => {
    if (onSchoolChange) onSchoolChange(value);
    if (setSchool) setSchool(value);
    logger.info(`School selected: ${value}`);
  };
  
  const updateCustomSchoolDistrict = (value: string) => {
    if (onCustomSchoolDistrictChange) onCustomSchoolDistrictChange(value);
    if (setCustomSchoolDistrict) setCustomSchoolDistrict(value);
    logger.info(`Custom district entered: ${value}`);
  };
  
  const updateCustomSchool = (value: string) => {
    if (onCustomSchoolChange) onCustomSchoolChange(value);
    if (setCustomSchool) setCustomSchool(value);
    logger.info(`Custom school entered: ${value}`);
  };
  
  // Fetch districts from the database
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('school_districts')
          .select('id, name')
          .order('name');
        
        if (error) {
          logger.info(`Error fetching districts: ${error.message}`);
          return;
        }
        
        if (data && data.length > 0) {
          const formattedDistricts = data.map(district => ({
            value: district.name,
            label: district.name,
          }));
          
          // Add "Other" option
          if (!formattedDistricts.some(d => d.value === "Other")) {
            formattedDistricts.push({ value: "Other", label: "Other (specify)" });
          }
          
          setDistrictsData(formattedDistricts);
          logger.info(JSON.stringify({ 
            fetchedDistrictsCount: formattedDistricts.length,
            districtsSource: "database" 
          }));
        } else {
          // Fallback to default districts if none in DB
          setDistrictsData([
            { value: "Other", label: "Other (specify)" }
          ]);
          logger.info("No districts found in database, using fallback");
        }
      } catch (error) {
        logger.info(`Exception fetching districts: ${error instanceof Error ? error.message : String(error)}`);
      } finally {
        setLoading(false);
      }
    };
    
    // Only fetch districts if not provided via props
    if (!providedDistricts || providedDistricts.length === 0) {
      fetchDistricts();
    } else {
      setDistrictsData(providedDistricts);
      setLoading(false);
      logger.info(JSON.stringify({ 
        usingProvidedDistricts: true, 
        districtCount: providedDistricts.length,
        districts: providedDistricts.map(d => d.label).join(', ')
      }));
    }
  }, [providedDistricts]);
  
  // Fetch schools from database when schoolDistrict changes
  useEffect(() => {
    const fetchSchools = async () => {
      if (!schoolDistrict || schoolDistrict === "Other") {
        setSchoolsData([]);
        return;
      }
      
      try {
        // Get district ID first
        const { data: districtData, error: districtError } = await supabase
          .from('school_districts')
          .select('id')
          .eq('name', schoolDistrict)
          .maybeSingle();
        
        if (districtError) {
          logger.info(`Error finding district ID: ${districtError.message}`);
          return;
        }
        
        if (!districtData) {
          logger.info(`No district found with name: ${schoolDistrict}`);
          setSchoolsData([{ value: "Other", label: "Other (specify)" }]);
          return;
        }
        
        // Fetch schools for this district
        const { data: schoolsData, error: schoolsError } = await supabase
          .from('schools')
          .select('id, name')
          .eq('district_id', districtData.id)
          .order('name');
        
        if (schoolsError) {
          logger.info(`Error fetching schools: ${schoolsError.message}`);
          return;
        }
        
        if (schoolsData && schoolsData.length > 0) {
          const formattedSchools = schoolsData.map(school => ({
            value: school.name,
            label: school.name,
          }));
          
          // Add "Other" option
          if (!formattedSchools.some(s => s.value === "Other")) {
            formattedSchools.push({ value: "Other", label: "Other (specify)" });
          }
          
          setSchoolsData(formattedSchools);
          logger.info(JSON.stringify({ 
            fetchedSchoolsCount: formattedSchools.length, 
            districtsSource: "database",
            forDistrict: schoolDistrict
          }));
        } else {
          // If no schools found, just provide "Other"
          setSchoolsData([{ value: "Other", label: "Other (specify)" }]);
          logger.info(`No schools found for district: ${schoolDistrict}`);
        }
      } catch (error) {
        logger.info(`Exception fetching schools: ${error instanceof Error ? error.message : String(error)}`);
        setSchoolsData([{ value: "Other", label: "Other (specify)" }]);
      }
    };
    
    // Only fetch schools if not provided via props
    if (schoolDistrict && (!providedSchools || providedSchools.length === 0)) {
      fetchSchools();
    } else if (providedSchools && providedSchools.length > 0) {
      setSchoolsData(providedSchools);
      logger.info(JSON.stringify({ 
        usingProvidedSchools: true,
        schoolsCount: providedSchools.length,
        schools: providedSchools.map(s => s.label).join(', ')
      }));
    }
  }, [schoolDistrict, providedSchools]);

  // Determine final districts and schools to use
  const districts = districtsData.length > 0 ? districtsData : [];
  const schools = schoolsData.length > 0 ? schoolsData : [];

  // Ensure districts includes "Other" option
  const districtsWithDefaults = districts.some(d => d.value === "Other") 
    ? districts 
    : [...districts, { value: "Other", label: "Other (specify)" }];

  // Ensure schools includes "Other" option
  const schoolsWithDefaults = schools.some(s => s.value === "Other")
    ? schools
    : [...schools, { value: "Other", label: "Other (specify)" }];

  useEffect(() => {
    // Update the visibility of the custom district field whenever district changes
    setShowCustomDistrict(schoolDistrict === "Other");
    logger.info(JSON.stringify({ 
      schoolDistrict, 
      showCustomDistrict: schoolDistrict === "Other" 
    }));
  }, [schoolDistrict]);

  useEffect(() => {
    // Update the visibility of the custom school field whenever school changes
    setShowCustomSchool(school === "Other");
    logger.info(JSON.stringify({ 
      school, 
      showCustomSchool: school === "Other" 
    }));
  }, [school]);

  const handleDistrictChange = (value: string) => {
    updateSchoolDistrict(value);
    setShowCustomDistrict(value === "Other");
    // Reset school selection when district changes
    if (value !== schoolDistrict) {
      logger.info(JSON.stringify({ 
        oldDistrict: schoolDistrict, 
        newDistrict: value 
      }));
      updateSchool("");
      setShowCustomSchool(false);
    }
  };

  const handleSchoolChange = (value: string) => {
    updateSchool(value);
    setShowCustomSchool(value === "Other");
  };

  const isActuallyLoading = loading || isLoadingDistricts;

  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="schoolDistrict" className="text-[#004161] font-serif">School District</Label>
        <Combobox
          value={schoolDistrict}
          options={districtsWithDefaults}
          onChange={handleDistrictChange}
          disabled={disabled || isLoadingDistricts}
          placeholder="Select your school district"
          className="bg-transparent border-[#004161]/10 focus:border-[#004161]/20 focus:ring-0 transition-all duration-300 placeholder:text-[#004161]/40 rounded-xl font-serif"
        />
        {showCustomDistrict && (
            <Input
            id="customSchoolDistrict"
            type="text"
            placeholder="Enter your school district"
              value={customSchoolDistrict}
              onChange={(e) => updateCustomSchoolDistrict(e.target.value)}
            required
              disabled={disabled}
            className="mt-2 bg-transparent border-[#004161]/10 focus:border-[#004161]/20 focus:ring-0 transition-all duration-300 placeholder:text-[#004161]/40 rounded-xl font-serif"
            />
        )}
      </div>
      
      <div className="space-y-1.5">
        <Label htmlFor="school" className="text-[#004161] font-serif">School</Label>
        <Combobox
          value={school}
          options={schoolsWithDefaults}
          onChange={handleSchoolChange}
          disabled={disabled || !schoolDistrict}
          placeholder="Select your school"
          className="bg-transparent border-[#004161]/10 focus:border-[#004161]/20 focus:ring-0 transition-all duration-300 placeholder:text-[#004161]/40 rounded-xl font-serif"
        />
        {showCustomSchool && (
            <Input
              id="customSchool"
            type="text"
            placeholder="Enter your school"
              value={customSchool}
              onChange={(e) => updateCustomSchool(e.target.value)}
            required
              disabled={disabled}
            className="mt-2 bg-transparent border-[#004161]/10 focus:border-[#004161]/20 focus:ring-0 transition-all duration-300 placeholder:text-[#004161]/40 rounded-xl font-serif"
            />
        )}
      </div>
    </>
  );
}
